import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHelmet from '@/components/feature/SEOHelmet';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { featuredResources, resources } from '@/mocks/resources';
import { faqs } from '@/mocks/faq';

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors duration-150"
        onClick={() => setOpen(!open)}
      >
        <span className="text-sm font-medium text-foreground-800 pr-4">{question}</span>
        <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-foreground-500 text-lg"></i>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-4">
          <p className="text-sm text-foreground-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  return (
    <>
      <SEOHelmet
        title="宅宅资源库 - 二次元资源聚合平台 | 动漫漫画游戏轻小说一站式下载"
        description="宅宅资源库是专为二次元爱好者打造的资源聚合平台，涵盖动漫、漫画、游戏、轻小说等海量宅系资源，一站式满足你的宅文化需求。每日更新，免费下载，让每位宅友都能快速找到心仪内容。"
        keywords="宅宅资源库,二次元资源下载,动漫资源聚合,宅文化平台,漫画资源库,轻小说资源,游戏资源合集"
        canonicalUrl="https://doujincenter.github.io"
        ogType="website"
      />
      <SchemaOrg type="Organization" data={{
        name: '宅宅资源库',
        url: 'https://doujincenter.github.io',
        description: '专为二次元爱好者打造的资源聚合平台',
        sameAs: ['https://t.me/zhazhai_resources'],
        contactPoint: { '@type': 'ContactPoint', email: 'contact@zhazhai.moe', contactType: 'customer service' },
      }} />
      <SchemaOrg type="WebSite" data={{
        name: '宅宅资源库',
        url: 'https://doujincenter.github.io',
        potentialAction: { '@type': 'SearchAction', target: 'https://doujincenter.github.io/search?q={search_term_string}', 'query-input': 'required name=search_term_string' },
      }} />
      <SchemaOrg type="WebPage" data={{
        name: '宅宅资源库 - 首页',
        url: 'https://doujincenter.github.io',
        isPartOf: { '@type': 'WebSite', name: '宅宅资源库', url: 'https://doujincenter.github.io' },
      }} />

      <Navbar transparent />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[620px] md:h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Vibrant%20colorful%20anime%20style%20illustration%20with%20cherry%20blossoms%20floating%20in%20the%20air%2C%20warm%20pink%20and%20golden%20sunset%20sky%2C%20silhouettes%20of%20anime%20characters%20in%20the%20distance%2C%20Japanese%20urban%20landscape%2C%20dreamy%20and%20nostalgic%20atmosphere%2C%20soft%20bokeh%20effects%2C%20stylized%20artistic%20rendering&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
            alt="宅宅资源库 - 二次元资源聚合平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                宅宅资源库
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                专为二次元爱好者打造的资源聚合平台<br />
                动漫 · 漫画 · 游戏 · 轻小说 · 音乐 · 壁纸
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/anime"
                  className="px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/25"
                >
                  立即探索
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-lg bg-white/15 text-white font-medium text-sm hover:bg-white/25 backdrop-blur-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center justify-center gap-6 mt-10">
                {[
                  { value: '128万+', label: '注册用户' },
                  { value: '5,937+', label: '资源总数' },
                  { value: '35万+', label: '日均访问' },
                ].map(stat => (
                  <div key={stat.label} className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-xs text-white/60 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">探索资源分类</h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">六大资源分类，覆盖你的所有宅文化需求</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-100 group-hover:bg-primary-200 transition-colors duration-200">
                    <i className={`${cat.icon} text-primary-600 text-2xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-400">{cat.description.slice(0, 12)}...</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Resources */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">热门资源</h2>
                <p className="text-sm text-foreground-500">社区最受欢迎的资源精选</p>
              </div>
              <Link
                to="/category/anime"
                className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-150 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {featuredResources.slice(0, 8).map(resource => (
                <Link
                  key={resource.id}
                  to={`/article/${resource.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200 cursor-pointer"
                >
                  <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      title={`${resource.title} - 宅宅资源库`}
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                      {resource.rating}
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm font-medium text-foreground-800 line-clamp-2 mb-2 leading-snug group-hover:text-primary-600 transition-colors duration-150">
                      {resource.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{resource.size}</span>
                      <span>{resource.downloads} 下载</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">最新更新</h2>
              <p className="text-sm text-foreground-500">每日更新，不错过任何精彩内容</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.filter(r => !r.featured).slice(0, 6).map(resource => (
                <Link
                  key={resource.id}
                  to={`/article/${resource.slug}`}
                  className="flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-full object-cover object-top"
                      title={`${resource.title} - 宅宅资源库`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground-800 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-150">
                      {resource.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2 line-clamp-1">{resource.desc}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {resource.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="w-full px-4 md:px-6 py-14 bg-primary-500">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
              {[
                { value: '128万+', label: '注册用户' },
                { value: '5,937+', label: '资源总数' },
                { value: '86万+', label: '社区帖子' },
                { value: '99.9%', label: '可用率' },
              ].map(stat => (
                <div key={stat.label}>
                  <div className="text-3xl md:text-4xl font-bold text-background-50">{stat.value}</div>
                  <div className="text-sm text-background-50/70 mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">常见问题</h2>
              <p className="text-sm text-foreground-500">关于宅宅资源库，你可能想了解的内容</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqs.map((faq, idx) => (
                <FAQItem key={idx} question={faq.question} answer={faq.answer} />
              ))}
            </div>
            <SchemaOrg type="FAQPage" data={{
              mainEntity: faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
              })),
            }} />
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full px-4 md:px-6 py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20anime%20style%20night%20sky%20with%20stars%20and%20colorful%20aurora%2C%20silhouettes%20of%20buildings%20below%2C%20dreamy%20and%20inspirational%20atmosphere%2C%20warm%20golden%20and%20pink%20gradient%2C%20stylized%20artistic%20rendering&width=1600&height=600&seq=cta-bg-01&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/70 to-primary-950/80"></div>
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">加入百万宅友的行列</h2>
            <p className="text-sm text-white/70 mb-8 leading-relaxed">海量资源每日更新，社区氛围温暖友好，快来和全球宅友一起交流分享吧！</p>
            <Link
              to="/about"
              className="inline-flex px-8 py-3 rounded-lg bg-accent-500 text-background-50 font-medium text-sm hover:bg-accent-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              关于宅宅资源库
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}